/**
 * Subscription Service
 * Handles communication with backend API for Stripe subscriptions
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface CreateSubscriptionResponse {
  clientSecret: string;
  customerId: string;
  setupIntentId: string;
}

export interface SubscriptionStatus {
  active: boolean;
  subscription: {
    id: string;
    status: string;
    current_period_end: number;
    cancel_at_period_end: boolean;
  } | null;
}

/**
 * Create a new subscription
 */
export const createSubscription = async (
  userId: string,
  email: string,
  firstName: string,
  lastName: string
): Promise<CreateSubscriptionResponse> => {
  const response = await fetch(`${API_URL}/create-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userId,
      email,
      firstName,
      lastName,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create subscription' }));
    throw new Error(error.error || 'Failed to create subscription');
  }

  const data = await response.json();
  
  if (!data.clientSecret) {
    console.error('❌ No client secret in response:', data);
    throw new Error('Server did not return a setup client secret. Please try again or contact support.');
  }

  return data;
};

/**
 * Complete subscription after payment method is attached
 */
export const completeSubscription = async (
  setupIntentId: string,
  customerId: string,
  userId: string
): Promise<{ subscriptionId: string; status: string }> => {
  const response = await fetch(`${API_URL}/complete-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      setupIntentId,
      customerId,
      userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to complete subscription' }));
    throw new Error(error.error || 'Failed to complete subscription');
  }

  return response.json();
};

/**
 * Check subscription status for a user
 */
export const checkSubscriptionStatus = async (
  userId: string
): Promise<SubscriptionStatus> => {
  const response = await fetch(`${API_URL}/subscription-status/${userId}`);

  if (!response.ok) {
    throw new Error('Failed to check subscription status');
  }

  return response.json();
};



